import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import FavoriteButton from '../../../components/ui/FavoriteButton';

const SpotCard = ({ spot, showDistance = false }) => {
  const navigate = useNavigate();

  const getDifficultyColor = (difficulty) => {
    const colors = {
      'Başlangıç': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      'Orta': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      'İleri': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
    };
    return colors?.[difficulty] || colors?.['Orta'];
  };

  // CRITICAL FIX: Aggressive fail-safe image source determination
  // Priority: spot.image_url (DB) → spot.imageUrl (service) → spot.image → spot.gallery_urls[0] (DB) → spot.gallery[0] → placeholder
  const getDisplayImage = () => {
    // Check all possible image sources in priority order
    // Database field (snake_case) takes priority
    const imageUrl = spot?.image_url || spot?.imageUrl || spot?.image || 
                     (spot?.gallery_urls && spot?.gallery_urls?.length > 0 ? spot?.gallery_urls?.[0] : null) ||
                     (spot?.gallery && spot?.gallery?.length > 0 ? spot?.gallery?.[0] : null);
    
    // If no image found, return placeholder
    if (!imageUrl) {
      return '/assets/images/no_image.png';
    }
    
    // If image is a relative path (starts with /), it's already a Supabase URL or local asset
    // AppImage component should handle Supabase URLs automatically
    return imageUrl;
  };

  const displayImage = getDisplayImage();

  const handleCardClick = () => {
    navigate(`/spot-details/${spot?.id}`);
  };

  return (
    <div
      onClick={handleCardClick}
      className="bg-card rounded-xl overflow-hidden shadow-organic hover:shadow-card-hover transition-all duration-300 cursor-pointer group"
    >
      <div className="relative w-full h-64 overflow-hidden bg-muted">
        {displayImage === '/assets/images/no_image.png' ? (
          // Visual fallback with icon instead of broken image
          (<div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900">
            <Icon name="ImageOff" size={48} className="text-gray-400 mb-2" />
            <span className="text-sm text-gray-500 dark:text-gray-400">Fotoğraf yok</span>
          </div>)
        ) : (
          <Image
            src={displayImage}
            alt={spot?.imageAlt || `${spot?.name} paddle board spot`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        )}
        
        <FavoriteButton itemId={spot?.id} itemType="spot" />
        
        <div className="absolute top-3 left-3 flex gap-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(spot?.difficulty)}`}>
            {spot?.difficulty}
          </span>
          {spot?.featured && (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-accent text-accent-foreground">
              Öne Çıkan
            </span>
          )}
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
          {spot?.name}
        </h3>
        
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
          <Icon name="MapPin" size={16} />
          <span>{spot?.location}</span>
        </div>

        {/* Distance Display (Nearby Mode) */}
        {showDistance && spot?.distanceKm && (
          <div className="flex items-center gap-2 mb-3 p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
            <Icon name="Navigation" size={16} className="text-indigo-600 dark:text-indigo-400" />
            <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">
              {spot?.distanceKm} km uzakta
            </span>
          </div>
        )}

        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1">
            <Icon name="Star" size={16} className="text-yellow-500 fill-yellow-500" />
            <span className="text-sm font-medium text-foreground">{spot?.rating}</span>
            <span className="text-sm text-muted-foreground">({spot?.reviewCount})</span>
          </div>
          
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <Icon name="Users" size={16} />
            <span>{spot?.recentVisitors} ziyaretçi</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="flex items-center gap-1 text-muted-foreground">
            <Icon name="Car" size={14} />
            <span className="truncate">{spot?.parking}</span>
          </div>
          <div className="flex items-center gap-1 text-muted-foreground">
            <Icon name="Waves" size={14} />
            <span className="truncate">{spot?.waterCondition}</span>
          </div>
        </div>

        <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
          <span className="text-sm font-medium text-primary">{spot?.entryFee}</span>
          <div className="flex items-center gap-1 text-sm text-primary group-hover:gap-2 transition-all">
            <span>Detayları Gör</span>
            <Icon name="ArrowRight" size={16} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpotCard;